import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import { useGLTF, Center, OrbitControls, RandomizedLight, MeshRefractionMaterial, useEnvironment, Environment, ContactShadows } from '@react-three/drei';
import React, { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from "react-colorful";
import { proxy, useSnapshot } from "valtio";

// Configurator state with default colors
const state = proxy({
  current: null,
  items: { ringBody: "#fff0f0", diamond: "#ffffff" },
});

function Ring({ env, ...props }) {
  const { nodes, materials } = useGLTF('/Ring_2-transformed.glb');
  const snap = useSnapshot(state);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (nodes) {
      console.log("Loaded nodes:", nodes);
      if (!nodes.RingBody001 || !nodes.Diamond001) {
        console.error("Required meshes are missing from the GLTF model.");
      }
    }
  }, [nodes]);

  useEffect(() => {
    const cursor = `<svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><path fill="rgba(255, 255, 255, 0.5)" d="M29.5 54C43.031 54 54 43.031 54 29.5S43.031 5 29.5 5 5 15.969 5 29.5 15.969 54 29.5 54z" stroke="#000"/><g filter="url(#filter0_d)"><path d="M29.5 47C39.165 47 47 39.165 47 29.5S39.165 12 29.5 12 12 19.835 12 29.5 19.835 47 29.5 47z" fill="${snap.items[hovered]}"/></g><path d="M2 2l11 2.947L4.947 13 2 2z" fill="#000"/><text fill="#000" style="white-space:pre" font-family="Inter var, sans-serif" font-size="10" letter-spacing="-.01em"><tspan x="15" y="63">${hovered}</tspan></text></g><defs><clipPath id="clip0"><path fill="#fff" d="M0 0h64v64H0z"/></clipPath><filter id="filter0_d" x="6" y="8" width="47" height="47" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="2"/><feGaussianBlur stdDeviation="3"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/><feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/></filter></defs></svg>`;
    const auto = `<svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="rgba(255, 255, 255, 0.5)" d="M29.5 54C43.031 54 54 43.031 54 29.5S43.031 5 29.5 5 5 15.969 5 29.5 15.969 54 29.5 54z" stroke="#000"/><path d="M2 2l11 2.947L4.947 13 2 2z" fill="#000"/></svg>`;
    
    if (hovered) {
      document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(cursor)}'), auto`;
      return () => (document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(auto)}'), auto`);
    }
  }, [hovered, snap.items]);

  return (
    <group {...props} dispose={null}>
      <mesh 
        castShadow receiveShadow 
        geometry={nodes.RingBody001.geometry} 
        material={materials.RingBody} 
        onPointerOver={() => setHovered("ringBody")} 
        onPointerOut={() => setHovered(null)} 
        onClick={(e) => {
          e.stopPropagation();
          state.current = "ringBody";
        }}
      >
        <meshStandardMaterial color={snap.items.ringBody} roughness={0.15} metalness={1} envMapIntensity={1.5} />
      </mesh>
      <mesh 
        castShadow receiveShadow 
        geometry={nodes.Diamond001.geometry} 
        material={materials.Diamond} 
        onPointerOver={() => setHovered("diamond")} 
        onPointerOut={() => setHovered(null)} 
        onClick={(e) => {
          e.stopPropagation();
          state.current = "diamond";
        }}
      >
        <MeshRefractionMaterial color={snap.items.diamond} side={THREE.DoubleSide} envMap={env} aberrationStrength={0.02} toneMapped={false} />
      </mesh>
    </group>
  );
}

export default function App() {
  const env = useEnvironment({ files: process.env.PUBLIC_URL + '/peppermint_powerplant_2_1k_adjusted.hdr' });
  const controlsRef = useRef();
  const canvasRef = useRef();

  const handleClickOutside = (e) => {
    if (!e.target.closest('.picker') && !e.target.closest('mesh')) {
      state.current = null;
    }
  };

  useEffect(() => {
    document.addEventListener('pointerdown', handleClickOutside);

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, []);

  return (
    <div className="viewer-container" ref={canvasRef}>
      <h1 className="viewer-heading">Transform Your Online Store With Cela 3D Visualization</h1>
      <p className="viewer-subheading">Interactive, lifelike product displays to increase engagement and sales</p>
      <div className="canvas-container">
        <Canvas shadows dpr={[1, 2]} gl={{ antialias: true }} camera={{ position: [-5, 5, 14], fov: 20 }}>
          <color attach="background" args={['#f0f0f']} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, -20, 5]} intensity={0.7} castShadow />
          <directionalLight position={[0, 5, 5]} intensity={0.7} castShadow />
          <Center top position={[0, -0.05, 0]} rotation={[-0.05, 0, 0.005]}>
            <Ring env={env} scale={0.1} />
          </Center>
          <ContactShadows position={[0, 0, 0]} frames={100} opacity={0.7} blur={1.5}>
            <RandomizedLight amount={8} radius={10} ambient={0.5} position={[1, 5, -1]} />
          </ContactShadows>
          <OrbitControls ref={controlsRef} autoRotate enableZoom={false} enablePan={false} minPolarAngle={0} maxPolarAngle={Math.PI / 2.25} />
          <Environment map={env} blur={1} />
        </Canvas>
      </div>
      <Picker /> {/* Integrating the color picker component */}
      <a href="https://calendly.com/vincent-119" target="_blank" rel="noopener noreferrer" className="viewer-demo-button">
        Book a Demo
      </a>
    </div>
  );
}

function Picker() {
  const snap = useSnapshot(state);

  return (
    <div
      className="picker"
      style={{ display: snap.current ? "block" : "none", position: "absolute", top: "35vh", left: "40vw", zIndex: 10 }}
    >
      <HexColorPicker 
        color={snap.items[snap.current]} 
        onChange={(color) => (state.items[snap.current] = color)} 
        style={{ width: "90px", height: "90px" }} // Set width and height for the color picker
      />
      <h2 style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 300 }}>{snap.current}</h2>
    </div>

  );
}
